import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCheck, Inbox } from "lucide-react";
import api from "@/lib/api";
import NotificationItem from "@/components/notifications/NotificationItem";
import { resolveNotificationUrl } from "@/lib/notificationRoutes";

export default function NotificationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const userRole = user?.role || "student";
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get("/notifications", { params: { limit: 50 } });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      /* silencieux */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.notification_id === id ? { ...n, is_read: true } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      /* silencieux */
    }
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await api.post("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      /* silencieux */
    } finally {
      setMarkingAll(false);
    }
  };

  const handleClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.notification_id);
    }
    const url = resolveNotificationUrl(notification, userRole);
    navigate(url);
  };

  return (
    <div data-testid="notifications-page" className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">
            Notifications
          </h1>
          {unreadCount > 0 && (
            <p className="text-sm text-slate-500 mt-1">
              {unreadCount} non lue{unreadCount > 1 ? "s" : ""}
            </p>
          )}
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={markAllRead}
            disabled={markingAll}
            className="gap-1.5"
          >
            <CheckCheck className="w-4 h-4" />
            Tout marquer comme lu
          </Button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
        {loading ? (
          <div className="py-16 text-center text-sm text-slate-400">Chargement…</div>
        ) : notifications.length === 0 ? (
          <div className="py-16 text-center px-4">
            <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Aucune notification</p>
            <p className="text-xs text-slate-400 mt-1">Vous serez alerté ici des nouveautés</p>
          </div>
        ) : (
          notifications.map((n) => (
            <NotificationItem key={n.notification_id} notification={n} onClick={handleClick} />
          ))
        )}
      </div>
    </div>
  );
}